import React from 'react';
import { KBBlock } from '../../../types';
import { renderInline } from '../utils/renderInline';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

const ListBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const ordered = block.type === 'ordered_list';
  const items: string[] = (block.data?.items ?? ['']).map((it: any) =>
    typeof it === 'string' ? it : it?.content ?? ''
  );

  const setItems = (next: string[]) => onChange({ data: { ...block.data, items: next } });

  const updateItem = (i: number, val: string) => {
    const next = [...items];
    next[i] = val;
    setItems(next);
  };

  const addItem = () => setItems([...items, '']);
  const removeItem = (i: number) => setItems(items.filter((_, idx) => idx !== i));

  const handleKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'Enter') { e.preventDefault(); addItem(); }
    if (e.key === 'Backspace' && items[i] === '' && items.length > 1) { e.preventDefault(); removeItem(i); }
    if (e.key === 'Escape') onFinish();
  };

  if (isEditing) {
    return (
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 group">
            <span className="text-gray-400 text-sm w-5 flex-shrink-0">
              {ordered ? `${i + 1}.` : '•'}
            </span>
            <input
              autoFocus={i === items.length - 1}
              type="text"
              value={item}
              placeholder="List item"
              onChange={(e) => updateItem(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700"
            />
            <button onClick={() => removeItem(i)} className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 text-xs">×</button>
          </div>
        ))}
        <button onClick={addItem} className="text-xs text-blue-500 hover:underline ml-7">+ Add item</button>
        <div className="pt-1 ml-7">
          <button onClick={onFinish} className="text-xs bg-blue-600 text-white px-3 py-1 rounded">Done</button>
        </div>
      </div>
    );
  }

  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag className={`space-y-1 pl-5 ${ordered ? 'list-decimal' : 'list-disc'}`}>
      {items.map((item, i) => (
        <li key={i} className="text-sm text-gray-700 leading-relaxed">
          {item ? renderInline(item) : <em className="text-gray-300">Empty item</em>}
        </li>
      ))}
    </Tag>
  );
};

export default ListBlock;
